// The generic "act" surface: a drawer with the command's schema form. Every page that offers an action uses it, so every
// act is one catalogue command through the inbox, with the reason shown when the person may not ask for it.
import { useState } from "react";
import { Drawer, ErrorState, SchemaForm } from "../components";
import { newIdempotencyKey } from "../data/source";
import type { InboxRow, ToFill } from "../data/types";
import { mayRequest } from "../lib/commands";
import { markSent } from "../lib/settled";
import { useApp } from "./context";
import { useEngine } from "./engine";

/** `toFill`: what the engine's action says a person must still supply (its hints and required marks shape the form). */
/** `settles`: the Action center item (settleKey) this act disposes of — it shows as sent, and leaves once the engine has done it. */
export interface ActionIntent { command: string; engagementId: string | null; fixed?: Record<string, unknown>; initial?: Record<string, unknown>; title: string; hide?: string[]; toFill?: ToFill[]; settles?: string }

export function ActionDrawer({ intent, onClose, onSent }: { intent: ActionIntent | null; onClose: () => void; onSent?: (row: InboxRow) => void }) {
  const { catalogue, me } = useApp();
  const request = useEngine();
  const [key, setKey] = useState(newIdempotencyKey);         // one key per intended act: a double submit is not a second act
  const [busy, setBusy] = useState(false);
  const spec = intent ? catalogue.get(intent.command) : undefined;
  const allowed = intent ? mayRequest(spec, me, intent.engagementId ? "engagement" : "firm") : null;
  return (
    <Drawer open={!!intent} onClose={onClose} title={intent?.title ?? ""}>
      {intent && (
        <div className="space-y-3">
          <p className="font-mono text-xs text-muted">{intent.command}{spec ? ` — ${spec.summary}` : ""}</p>
          {!allowed?.ok ? <ErrorState error={allowed?.reason ?? "Not available."} /> : (
            <SchemaForm spec={spec!} fixed={intent.fixed} initial={intent.initial} hideFields={intent.hide} toFill={intent.toFill} personName={me.personName} busy={busy} onCancel={onClose}
              onSubmit={async (payload) => {
                setBusy(true);
                const row = await request({ command: intent.command, engagementId: intent.engagementId, payload, what: intent.title, idempotencyKey: key });
                setBusy(false);
                if (row) { if (intent.settles) markSent(intent.settles, row); setKey(newIdempotencyKey()); onSent?.(row); onClose(); }
              }} />
          )}
        </div>
      )}
    </Drawer>
  );
}
